import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, Radio, Lightbulb, Megaphone } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";

const IMG =
  "https://images.unsplash.com/photo-1633933757597-2d09c360c2ad?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwyfHxtdXNpYyUyMHN0dWRpbyUyMG5lb24lMjBsaWdodHMlMjBhbGJ1bSUyMGNvdmVyJTIwYXJ0aXN0JTIwcmVjb3JkaW5nJTIwbWljcm9waG9uZSUyMHN5bnRofGVufDB8fHx8MTc5MTUxOTQ5NHww&ixlib=rb-4.1.0&q=85";

export const BusinessHero = () => {
  const { t } = useLanguage();
  return (
      <section className="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <div className="fade-up">
            <p className="eyebrow">{t("Business")}</p>
            <h1 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-slate-50 sm:text-4xl lg:text-5xl">
              {t("Radio jingles your audience can't skip")}
            </h1>
            <p className="mt-5 max-w-lg text-base leading-relaxed text-slate-400">
              {t("Custom advertising jingles and sonic branding for radio, TV and digital campaigns — composed, recorded and mastered in our Swiss studio. With optional concept & idea development by experienced copywriters.")}
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/order?package=business" data-testid="business-hero-order-cta" className="btn-primary">
                {t("Request a Jingle")} <ArrowRight size={16} />
              </Link>
              <Link to="/contact" data-testid="business-hero-contact-cta" className="btn-ghost">
                <Megaphone size={16} /> {t("Discuss Your Campaign")}
              </Link>
            </div>
          </div>
          <div className="studio-card fade-up overflow-hidden" style={{ animationDelay: "0.15s" }}>
            <img src={IMG} alt={t("Neon-lit studio microphone during a jingle recording session")} className="h-72 w-full object-cover sm:h-96" />
          </div>
        </div>
      </section>
  );
};

export const BusinessFeatures = () => {
  const { t } = useLanguage();
  return (
      <section className="border-y border-[#282C3D] bg-[#0E1018]">
        <div className="mx-auto max-w-6xl px-4 py-20 sm:px-6 lg:px-8">
          <p className="eyebrow">{t("What we deliver")}</p>
          <h2 className="mt-3 font-display text-2xl font-bold text-slate-100 sm:text-3xl">{t("Sonic identity for your brand")}</h2>
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {[
              { icon: Radio, title: "Radio & TV Jingles", text: "Memorable advertising stings and full-length spots, produced for broadcast standards and ready to air." },
              { icon: Lightbulb, title: "Concept & Idea Development", text: "Optional: our copywriters develop the creative concept and message before a single note is written." },
              { icon: Megaphone, title: "Audio Logo & Licensing", text: "A recognizable sonic brand mark, delivered with commercial broadcasting licensing rights." },
            ].map((c, i) => (
              <div key={c.title} data-testid={`business-feature-${i + 1}`} className="studio-card fade-up p-6" style={{ animationDelay: `${i * 0.08}s` }}>
                <c.icon className="text-[#E5A93C]" size={22} />
                <h3 className="mt-4 font-display text-lg font-semibold text-slate-100">{t(c.title)}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-400">{t(c.text)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
  );
};

export const BusinessQuote = () => {
  const { t } = useLanguage();
  return (
      <section className="mx-auto max-w-6xl px-4 py-20 sm:px-6 lg:px-8">
        <div className="studio-card flex flex-col items-start justify-between gap-8 p-8 sm:p-12 lg:flex-row lg:items-center">
          <div className="max-w-xl">
            <p className="eyebrow">{t("Pricing")}</p>
            <h2 className="mt-3 font-display text-2xl font-bold text-slate-50 sm:text-3xl">{t("By agreement — tailored to your campaign")}</h2>
            <p className="mt-3 text-sm leading-relaxed text-slate-400">
              {t("Every campaign is different: reach, media, runtime and licensing scope shape the price. Tell us about your project and receive a transparent, personal proposal from the studio.")}
            </p>
            <ul className="mt-5 space-y-2.5 text-sm text-slate-300">
              {["Fixed, written offer before production starts", "Broadcast licensing rights included", "Revisions until the spot fits"].map((f) => (
                <li key={f} className="flex gap-2.5">
                  <CheckCircle2 size={16} className="mt-0.5 shrink-0 text-[#10B981]" />
                  {t(f)}
                </li>
              ))}
            </ul>
          </div>
          <Link to="/order?package=business" data-testid="business-quote-cta" className="btn-primary shrink-0">
            {t("Get Your Proposal")} <ArrowRight size={16} />
          </Link>
        </div>
      </section>
  );
};

export default function Business() {
  return <div><BusinessHero /><BusinessFeatures /><BusinessQuote /></div>;
}
